import React, { useState, useEffect } from 'react';
import { myTournamentsAPI, familyArchersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateRange, getStatusColor, getOrgColor, getDaysUntil } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  CalendarDays,
  Users,
  User,
  AlertTriangle,
  Clock,
  MapPin,
  Target,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// Color palette for archers (distinct colors for up to 6 archers)
const ARCHER_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500' },
  { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', dot: 'bg-purple-500' },
  { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500' },
  { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300', dot: 'bg-rose-500' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-300', dot: 'bg-cyan-500' },
];

export default function FamilyDashboard() {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  const [archers, setArchers] = useState([]);
  const [allTournaments, setAllTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (isParent) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isParent]);

  const fetchData = async () => {
    try {
      const [archersRes, tournamentsRes] = await Promise.all([
        familyArchersAPI.getAll(),
        myTournamentsAPI.getAll() // Get all tournaments (no archer filter)
      ]);
      setArchers(archersRes.data);
      setAllTournaments(tournamentsRes.data);
    } catch (error) {
      toast.error('Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  // Get archer color by index or ID
  const getArcherColor = (archerId) => {
    const index = archers.findIndex(a => a.id === archerId);
    return ARCHER_COLORS[index % ARCHER_COLORS.length];
  };

  // Find scheduling conflicts (same date, different archers)
  const findConflicts = () => {
    const dateMap = {};
    const conflicts = [];

    allTournaments.forEach(t => {
      const start = new Date(t.start_date);
      const end = new Date(t.end_date);
      const current = new Date(start);

      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0];
        if (!dateMap[dateStr]) {
          dateMap[dateStr] = [];
        }
        dateMap[dateStr].push(t);
        current.setDate(current.getDate() + 1);
      }
    });

    // Find dates with multiple different archers (only consider events WITH archer assignments)
    Object.entries(dateMap).forEach(([date, tournaments]) => {
      // Filter to only events that have an archer assigned
      const assignedTournaments = tournaments.filter(t => t.archer_id && t.archer_name);
      const archerIds = [...new Set(assignedTournaments.map(t => t.archer_id))];
      
      if (archerIds.length > 1) {
        conflicts.push({
          date,
          tournaments: assignedTournaments,
          archers: archerIds.map(id => {
            const tournament = assignedTournaments.find(t => t.archer_id === id);
            return tournament?.archer_name || archers.find(a => a.id === id)?.name || 'Unknown';
          })
        });
      }
    });

    return conflicts.sort((a, b) => a.date.localeCompare(b.date));
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return allTournaments.filter(t => t.start_date <= dateStr && t.end_date >= dateStr);
  };

  // Get all dates with events, grouped by archer
  const eventDates = allTournaments.reduce((dates, t) => {
    const start = new Date(t.start_date);
    const end = new Date(t.end_date);
    const current = new Date(start);

    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      if (!dates[dateStr]) {
        dates[dateStr] = new Set();
      }
      dates[dateStr].add(t.archer_id || 'unassigned');
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, {});

  // Upcoming events sorted by date
  const today = new Date().toISOString().split('T')[0];
  const upcomingEvents = allTournaments
    .filter(t => t.end_date >= today)
    .sort((a, b) => a.start_date.localeCompare(b.start_date));

  // Group upcoming events by archer
  const eventsByArcher = archers.reduce((acc, archer) => {
    acc[archer.id] = upcomingEvents.filter(t => t.archer_id === archer.id);
    return acc;
  }, {});

  // Events without archer assignment
  const unassignedEvents = upcomingEvents.filter(t => !t.archer_id);

  const selectedDateEvents = getEventsForDate(selectedDate);
  const conflicts = findConflicts();
  const futureConflicts = conflicts.filter(c => c.date >= today);

  // Stats
  const totalEvents = upcomingEvents.length;
  const registeredCount = upcomingEvents.filter(t => t.tracking_status === 'REGISTERED').length;

  if (!isParent) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Family Dashboard</h1>
          <p className="text-slate-600 mt-1">Combined view of all family archers</p>
        </div>
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>
            This feature is only available for Parent accounts. Parent accounts can view a combined calendar and schedule for all family archers.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
          <p className="text-slate-600">Loading family dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="family-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Family Dashboard</h1>
          <p className="text-slate-600 mt-1">
            Combined view for {archers.length} archer{archers.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/family-archers">
          <Button variant="outline" data-testid="manage-archers-btn">
            <Users className="w-4 h-4 mr-2" />
            Manage Archers
          </Button>
        </Link>
      </div>

      {/* Empty State */}
      {archers.length === 0 && (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No archers in your family</h3>
            <p className="text-slate-500 mb-4 max-w-md mx-auto">
              Add your family archers to see their combined schedule and detect conflicts.
            </p>
            <Link to="/family-archers">
              <Button className="btn-primary">
                Add First Archer
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {archers.length > 0 && (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-base">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{archers.length}</p>
                    <p className="text-xs text-slate-500">Family Archers</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{totalEvents}</p>
                    <p className="text-xs text-slate-500">Upcoming Events</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CalendarDays className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{registeredCount}</p>
                    <p className="text-xs text-slate-500">Registered</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card-base">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${futureConflicts.length > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                    <AlertTriangle className={`w-5 h-5 ${futureConflicts.length > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{futureConflicts.length}</p>
                    <p className="text-xs text-slate-500">Conflicts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Conflicts Alert */}
          {futureConflicts.length > 0 && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Scheduling Conflicts Detected!</strong> You have {futureConflicts.length} date{futureConflicts.length > 1 ? 's' : ''} where multiple archers have overlapping events.
                <div className="mt-2 space-y-1">
                  {futureConflicts.slice(0, 3).map((conflict, i) => (
                    <div key={i} className="text-sm">
                      <span className="font-medium">{formatDate(conflict.date)}</span>: {conflict.archers.join(' & ')}
                    </div>
                  ))}
                  {futureConflicts.length > 3 && (
                    <p className="text-sm italic">+{futureConflicts.length - 3} more conflicts</p>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Archer Legend */}
          <Card className="card-base">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Archer Color Legend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {archers.map((archer, index) => {
                  const color = ARCHER_COLORS[index % ARCHER_COLORS.length];
                  const archerEvents = eventsByArcher[archer.id] || [];
                  return (
                    <div
                      key={archer.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${color.bg} ${color.border} border`}
                    >
                      <div className={`w-3 h-3 rounded-full ${color.dot}`} />
                      <span className={`font-medium ${color.text}`}>{archer.name}</span>
                      <Badge variant="secondary" className="text-xs">
                        {archerEvents.length} events
                      </Badge>
                    </div>
                  );
                })}
                {unassignedEvents.length > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-300">
                    <div className="w-3 h-3 rounded-full bg-slate-400" />
                    <span className="font-medium text-slate-600">Unassigned</span>
                    <Badge variant="secondary" className="text-xs">
                      {unassignedEvents.length} events
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <Card className="card-base lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-primary" />
                  Combined Family Calendar
                </CardTitle>
                <CardDescription>
                  Events are color-coded by archer. Multiple colors indicate multiple archers on the same day.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  className="rounded-md border w-full"
                  modifiers={{
                    hasEvent: (date) => {
                      const dateStr = date.toISOString().split('T')[0];
                      return eventDates[dateStr] && eventDates[dateStr].size > 0;
                    },
                    hasConflict: (date) => {
                      const dateStr = date.toISOString().split('T')[0];
                      return eventDates[dateStr] && eventDates[dateStr].size > 1;
                    }
                  }}
                  modifiersStyles={{
                    hasEvent: {
                      fontWeight: 'bold',
                      backgroundColor: 'hsl(215 100% 45% / 0.1)',
                      color: 'hsl(215 100% 45%)'
                    },
                    hasConflict: {
                      fontWeight: 'bold',
                      backgroundColor: 'hsl(45 100% 50% / 0.3)',
                      color: 'hsl(30 100% 30%)',
                      border: '2px solid hsl(45 100% 50%)'
                    }
                  }}
                  data-testid="family-calendar"
                />
                <div className="mt-4 flex items-center gap-4 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-primary/20" />
                    <span>Has events</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-300 border-2 border-amber-500" />
                    <span>Multiple archers (conflict)</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upcoming by Archer */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="text-lg">Upcoming by Archer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 max-h-[500px] overflow-y-auto">
                {archers.map((archer, index) => {
                  const color = ARCHER_COLORS[index % ARCHER_COLORS.length];
                  const archerEvents = eventsByArcher[archer.id] || [];
                  
                  return (
                    <div key={archer.id} className="space-y-2">
                      <div className={`flex items-center gap-2 px-2 py-1 rounded ${color.bg}`}>
                        <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                        <span className={`font-medium text-sm ${color.text}`}>{archer.name}</span>
                        <span className="text-xs text-slate-500">({archerEvents.length})</span>
                      </div>
                      {archerEvents.length > 0 ? (
                        <div className="space-y-1 pl-4">
                          {archerEvents.slice(0, 3).map(event => {
                            const daysUntil = getDaysUntil(event.start_date);
                            return (
                              <div
                                key={event.tracking_id}
                                className="text-sm p-2 bg-slate-50 rounded"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-slate-800 truncate flex-1">
                                    {event.name}
                                  </span>
                                  <Badge className={getStatusColor(event.tracking_status)} variant="secondary">
                                    {event.tracking_status}
                                  </Badge>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  {formatDate(event.start_date)}
                                  {daysUntil !== null && daysUntil >= 0 && (
                                    <span className="text-primary ml-1">
                                      ({daysUntil === 0 ? 'Today' : `in ${daysUntil}d`})
                                    </span>
                                  )}
                                </p>
                              </div>
                            );
                          })}
                          {archerEvents.length > 3 && (
                            <p className="text-xs text-slate-500 pl-2">
                              +{archerEvents.length - 3} more
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 pl-4 italic">No upcoming events</p>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Selected Date Events */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="text-lg">
                Events on {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDateEvents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedDateEvents.map(event => {
                    const archerColor = event.archer_id ? getArcherColor(event.archer_id) : null;
                    return (
                      <div
                        key={event.tracking_id}
                        className={`p-4 rounded-lg border ${archerColor ? `${archerColor.bg} ${archerColor.border}` : 'bg-slate-50 border-slate-200'}`}
                        data-testid={`family-event-${event.tracking_id}`}
                      >
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className={getOrgColor(event.org)}>{event.org}</Badge>
                          <Badge className={getStatusColor(event.tracking_status)}>
                            {event.tracking_status}
                          </Badge>
                          {event.archer_name && (
                            <Badge variant="secondary" className={archerColor ? `${archerColor.bg} ${archerColor.text}` : ''}>
                              <User className="w-3 h-3 mr-1" />
                              {event.archer_name}
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-semibold text-slate-900">{event.name}</h4>
                        <div className="mt-2 space-y-1 text-sm text-slate-500">
                          <p className="flex items-center gap-2">
                            <CalendarDays className="w-4 h-4" />
                            {formatDateRange(event.start_date, event.end_date)}
                          </p>
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {event.location}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CalendarDays className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No events on this date</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
